(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopOrbit = window.matchMedia('(min-width: 990px)');

  const moneyFromCents = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return (Number(cents || 0) / 100).toFixed(2);
    }
  };

  const initOrbit = (root) => {
    if (root.dataset.eliznaOrbitReady === 'true') return;
    root.dataset.eliznaOrbitReady = 'true';

    const nodes = Array.from(root.querySelectorAll('[data-orbit-node]'));
    const pills = Array.from(root.querySelectorAll('[data-orbit-mobile]'));
    const panels = Array.from(root.querySelectorAll('[data-orbit-panel]'));
    const viewport = root.querySelector('[data-orbit-viewport]');
    const circle = root.querySelector('[data-orbit-circle]');
    if (!nodes.length || !panels.length) return;

    const interval = Math.max(3000, Number(root.dataset.interval) || 4500);
    const desktopOrbit = window.matchMedia('(min-width: 990px)');
    let active = 0;
    let timer = null;
    let touchStartX = null;
    let rafId = null;

    const normalize = (index) => (index + panels.length) % panels.length;
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const setActive = (index, { focus = false } = {}) => {
      active = normalize(index);

      panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === active;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

      nodes.forEach((node, nodeIndex) => {
        const isActive = nodeIndex === active;
        node.classList.toggle('is-active', isActive);
        node.setAttribute('aria-selected', String(isActive));
        node.tabIndex = isActive ? 0 : -1;
      });

      pills.forEach((pill, pillIndex) => {
        const isActive = pillIndex === active;
        pill.classList.toggle('is-active', isActive);
        pill.setAttribute('aria-pressed', String(isActive));
      });

      if (focus && nodes[active]) nodes[active].focus({ preventScroll: true });
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const startMobileAutoplay = () => {
      stop();
      if (desktopOrbit.matches || reduceMotion.matches || document.hidden) return;
      timer = window.setInterval(() => setActive(active + 1), interval);
    };

    const positionNodes = (rotationDeg) => {
      if (!viewport || !circle || !desktopOrbit.matches) return;

      const viewportWidth = viewport.clientWidth;
      const viewportHeight = viewport.clientHeight;
      const circleSize = circle.offsetWidth;
      const centerX = circle.offsetLeft + (circleSize / 2);
      const centerY = circle.offsetTop + (circleSize / 2);
      const radius = circleSize * 0.49;

      nodes.forEach((node) => {
        const baseAngle = Number(node.dataset.orbitBaseAngle || 0);
        const angle = (baseAngle + rotationDeg) * Math.PI / 180;
        const x = centerX + (radius * Math.cos(angle));
        const y = centerY + (radius * Math.sin(angle));
        node.style.left = `${x}px`;
        node.style.top = `${y}px`;

        const rightSide = x > viewportWidth * .58;
        const upperSide = y < viewportHeight * .40;
        node.classList.toggle('is-right-side', rightSide);
        node.classList.toggle('is-upper-side', upperSide);
      });
    };

    const updateFromScroll = () => {
      rafId = null;
      if (!desktopOrbit.matches || !viewport || !circle) return;

      const rect = root.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const scrollable = Math.max(1, rect.height - vh * .55);
      const travelled = clamp((vh * .72) - rect.top, 0, scrollable);
      const progress = clamp(travelled / scrollable, 0, 1);

      // Full AB-like circular journey. The ring rotates continuously while the
      // active ingredient changes at five evenly spaced scroll checkpoints.
      const rotation = -24 + (progress * 218);
      root.style.setProperty('--orbit-rotation', `${rotation}deg`);
      positionNodes(rotation);

      const nextActive = Math.min(panels.length - 1, Math.round(progress * (panels.length - 1)));
      if (nextActive !== active) setActive(nextActive);
    };

    const requestScrollUpdate = () => {
      if (!desktopOrbit.matches || rafId !== null) return;
      rafId = window.requestAnimationFrame(updateFromScroll);
    };

    nodes.forEach((node, index) => {
      node.addEventListener('click', () => setActive(index));
      node.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') setActive(active + 1, { focus: true });
        else setActive(active - 1, { focus: true });
      });
    });

    pills.forEach((pill, index) => {
      pill.addEventListener('click', () => {
        setActive(index);
        startMobileAutoplay();
      });
    });

    root.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches?.[0]?.clientX ?? null;
    }, { passive: true });

    root.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const endX = event.changedTouches?.[0]?.clientX ?? touchStartX;
      const delta = endX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) < 45) return;
      setActive(delta < 0 ? active + 1 : active - 1);
      startMobileAutoplay();
    }, { passive: true });

    const onViewportModeChange = () => {
      stop();
      if (desktopOrbit.matches) requestScrollUpdate();
      else startMobileAutoplay();
    };

    if (typeof desktopOrbit.addEventListener === 'function') desktopOrbit.addEventListener('change', onViewportModeChange);
    else if (typeof desktopOrbit.addListener === 'function') desktopOrbit.addListener(onViewportModeChange);

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', requestScrollUpdate);
    document.addEventListener('visibilitychange', () => {
      if (desktopOrbit.matches) requestScrollUpdate();
      else startMobileAutoplay();
    });

    setActive(0);
    requestScrollUpdate();
    startMobileAutoplay();
  };

  const initEditorialHero = (root) => {
    if (root.dataset.eliznaHeroReady === 'true') return;
    root.dataset.eliznaHeroReady = 'true';

    const form = root.querySelector('[data-elizna-product-form]');
    const sticky = root.querySelector('[data-elizna-sticky-commerce]');
    const stickyButton = root.querySelector('[data-sticky-add]');
    const sellingPlan = root.querySelector('[data-elizna-selling-plan]');
    const purchaseCards = Array.from(root.querySelectorAll('.elizna-editorial-hero__purchase-card'));
    const purchaseOptions = Array.from(root.querySelectorAll('[data-purchase-option]'));
    const qty = root.querySelector('[data-elizna-quantity] input');
    const qtyMinus = root.querySelector('[data-qty-minus]');
    const qtyPlus = root.querySelector('[data-qty-plus]');

    purchaseOptions.forEach((radio) => {
      radio.addEventListener('change', () => {
        purchaseCards.forEach((card) => card.classList.toggle('is-selected', card.contains(radio) && radio.checked));
        if (sellingPlan) sellingPlan.disabled = radio.value !== 'subscription';
      });
    });

    qtyMinus?.addEventListener('click', () => {
      const min = Number(qty?.min || 1);
      if (qty) qty.value = String(Math.max(min, Number(qty.value || 1) - 1));
    });

    qtyPlus?.addEventListener('click', () => {
      if (qty) qty.value = String(Math.max(1, Number(qty.value || 1) + 1));
    });

    stickyButton?.addEventListener('click', () => {
      if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
      else root.querySelector('.elizna-editorial-hero__submit')?.click();
    });

    if (sticky) {
      sticky.hidden = false;
      const commerce = root.querySelector('[data-elizna-commerce]');
      const observerTarget = commerce || root;
      const observer = new IntersectionObserver((entries) => {
        const visible = !entries[0].isIntersecting && window.scrollY > 450;
        sticky.classList.toggle('is-visible', visible);
      }, { threshold: .08 });
      observer.observe(observerTarget);
    }

    if (form && !customElements.get('product-form')) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        submit?.setAttribute('aria-disabled', 'true');
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('Unable to add this item to the cart.');
          await response.json();
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        } catch (error) {
          const wrapper = form.querySelector('.product-form__error-message-wrapper');
          const message = form.querySelector('.product-form__error-message');
          if (wrapper && message) {
            message.textContent = error.message || 'Unable to add this item to the cart.';
            wrapper.hidden = false;
          }
        } finally {
          submit?.removeAttribute('aria-disabled');
        }
      });
    }
  };

  const scrollToIngredients = () => {
    const target = document.querySelector('.elizna-editorial-hero__details details[data-ingredients]');
    if (target) {
      target.open = true;
      target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-elizna-orbit]').forEach(initOrbit);
    scope.querySelectorAll('[data-elizna-editorial-hero]').forEach(initEditorialHero);

    scope.querySelectorAll('[data-scroll-ingredients]').forEach((button) => {
      if (button.dataset.eliznaIngredientsReady === 'true') return;
      button.dataset.eliznaIngredientsReady = 'true';
      button.addEventListener('click', scrollToIngredients);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
